import { create } from 'zustand'

/**
 * Idle lock state + real-activity tracking.
 *
 * Activity = actual input (pointer, keys, wheel, touch, scroll) in ANY tab;
 * background polling never counts. The last input time is shared across tabs
 * through localStorage and sent with every API call as X-Last-Activity so the
 * server enforces the same lock (authMiddleware). Lock / unlock in one tab is
 * mirrored to the others through the `storage` event.
 */
const ACTIVITY_KEY = 'pmms.lastActivity'
const LOCK_KEY = 'pmms.lockSignal'
/** localStorage is written at most this often while the user is active. */
const WRITE_EVERY_MS = 5_000

let lastWrite = 0

function readShared(): number {
  try {
    return Number(localStorage.getItem(ACTIVITY_KEY)) || 0
  } catch {
    return 0
  }
}

// Seeded from storage so reopening the app after a long gap still counts as idle.
let lastActivity = readShared() || Date.now()

/** Latest real input across tabs (ms epoch). */
export function getLastActivity(): number {
  return Math.max(lastActivity, readShared())
}

export function markActivity(now = Date.now(), { force = false } = {}): void {
  lastActivity = now
  if (!force && now - lastWrite < WRITE_EVERY_MS) return
  lastWrite = now
  try {
    localStorage.setItem(ACTIVITY_KEY, String(now))
  } catch {
    // storage blocked — in-memory value still works for this tab
  }
}

function signal(kind: 'lock' | 'unlock') {
  try {
    localStorage.setItem(LOCK_KEY, `${kind}:${Date.now()}`)
  } catch {
    // ignore
  }
}

interface LockState {
  locked: boolean
  /** Lock this tab (and tell the others). */
  lock: (opts?: { broadcast?: boolean }) => void
  unlock: (opts?: { broadcast?: boolean }) => void
}

export const useLockStore = create<LockState>((set, get) => ({
  locked: false,
  lock: ({ broadcast = true } = {}) => {
    if (get().locked) return
    set({ locked: true })
    if (broadcast) signal('lock')
  },
  unlock: ({ broadcast = true } = {}) => {
    markActivity(Date.now(), { force: true })
    set({ locked: false })
    if (broadcast) signal('unlock')
  },
}))

/** Resolves once this tab is unlocked (at once when it already is). */
export function waitForUnlock(): Promise<void> {
  if (!useLockStore.getState().locked) return Promise.resolve()
  return new Promise((resolve) => {
    const stop = useLockStore.subscribe((state) => {
      if (state.locked) return
      stop()
      resolve()
    })
  })
}

/** Fresh sign-in / sign-out: active now, not locked (no cross-tab signal). */
export function resetLockState(): void {
  markActivity(Date.now(), { force: true })
  useLockStore.setState({ locked: false })
}

/** Mirror lock / unlock from other tabs. Returns a cleanup function. */
export function listenForOtherTabs(): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key !== LOCK_KEY || !e.newValue) return
    const kind = e.newValue.split(':')[0]
    if (kind === 'lock') useLockStore.getState().lock({ broadcast: false })
    if (kind === 'unlock') useLockStore.getState().unlock({ broadcast: false })
  }
  window.addEventListener('storage', onStorage)
  return () => window.removeEventListener('storage', onStorage)
}
